import { useState } from "react";
import { Target, Gift, Check, Trash2, Plus } from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { rupiah, sum, fmtDate } from "@/lib/format";
import { EntryForm, ItemList, Section } from "./shared";

const GoalCard = ({ g, onAdd, onDelete }) => {
  const [amt, setAmt] = useState("");
  const pct = Math.min((g.saved / g.target) * 100, 100);
  return (
    <div className="rounded-3xl border border-pink-100 p-4 bg-white" data-testid="goal-card">
      <div className="flex justify-between items-start gap-2">
        <div>
          <p className="font-semibold text-gray-700">{g.name}</p>
          <p className="text-xs text-gray-400">{g.term === "short" ? "Jangka pendek" : "Jangka panjang"}{g.deadline ? ` · ${fmtDate(g.deadline)}` : ""}</p>
        </div>
        <button onClick={() => onDelete(g.id)} className="text-gray-300 hover:text-rose-500" data-testid="goal-delete" aria-label="Hapus"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="h-2.5 bg-pink-50 rounded-full mt-3 overflow-hidden"><div className="h-full bg-gradient-to-r from-pink-400 to-sky-400 rounded-full transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      <p className="text-xs text-gray-500 mt-1.5">{rupiah(g.saved)} dari {rupiah(g.target)} ({pct.toFixed(0)}%)</p>
      <div className="flex gap-2 mt-3">
        <input className="field !h-9" type="number" placeholder="Tambah (Rp)" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="goal-add-input" />
        <button disabled={!amt} onClick={async () => { await onAdd(g, Number(amt)); setAmt(""); }} className="px-3 rounded-2xl bg-pink-100 text-pink-600 hover:bg-pink-200 disabled:opacity-50" data-testid="goal-add-btn" aria-label="Tambah setoran"><Plus className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

const PRIO = { high: "Prioritas", medium: "Sedang", low: "Nanti aja" };

export default function GoalsTab() {
  const goals = useCollection("goals");
  const wish = useCollection("wishlist");
  const strip = ({ id, user_id, created_at, ...rest }) => rest;
  const addToGoal = (g, n) => goals.update(g.id, { ...strip(g), saved: g.saved + n });
  const toggleBought = (w) => wish.update(w.id, { ...strip(w), bought: !w.bought });

  return (
    <div className="space-y-4" data-testid="duitku-goals">
      <Section title={<span className="flex items-center gap-2"><Target className="w-5 h-5 text-pink-400" /> Target Keuanganku</span>}>
        <EntryForm
          testid="goal-form"
          initial={{ name: "", target: "", term: "short", deadline: "" }}
          fields={[
            { name: "name", label: "Nama target", required: true, placeholder: "mis. Liburan ke Jepang" },
            { name: "target", label: "Target (Rp)", type: "number", required: true, min: 1 },
            { name: "term", label: "Jangka", type: "select", options: [["short", "Pendek (< 1 thn)"], ["long", "Panjang"]] },
            { name: "deadline", label: "Tenggat", type: "date" },
          ]}
          onSubmit={(v) => goals.add({ ...v, target: Number(v.target), saved: 0 })}
        />
        <div className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="goal-list">
          {goals.items.length === 0 && <p className="text-sm text-gray-400 col-span-2 text-center py-4">Belum ada target. Mimpikan sesuatu yuk!</p>}
          {goals.items.map((g) => <GoalCard key={g.id} g={g} onAdd={addToGoal} onDelete={goals.remove} />)}
        </div>
      </Section>
      <Section title={<span className="flex items-center gap-2"><Gift className="w-5 h-5 text-sky-400" /> Barang Impianku</span>} action={<span className="text-xs text-gray-500" data-testid="wishlist-total">Total: {rupiah(sum(wish.items.filter((w) => !w.bought), "price"))}</span>}>
        <EntryForm
          testid="wish-form"
          initial={{ name: "", price: "", priority: "medium" }}
          fields={[
            { name: "name", label: "Barang impian", required: true, placeholder: "mis. Tas baru", wide: true },
            { name: "price", label: "Harga (Rp)", type: "number", required: true, min: 0 },
          ]}
          onSubmit={(v) => wish.add({ ...v, price: Number(v.price) })}
        />
        <div className="mt-4">
          <ItemList
            testid="wish-list"
            items={wish.items}
            onDelete={wish.remove}
            empty="Daftar barang impian masih kosong."
            render={(w) => ({
              icon: (
                <button onClick={() => toggleBought(w)} data-testid="wish-toggle" aria-label="Tandai terbeli" className={`w-8 h-8 rounded-full border-2 grid place-items-center shrink-0 transition-colors ${w.bought ? "bg-pink-500 border-pink-500 text-white" : "border-pink-200 text-transparent hover:border-pink-400"}`}>
                  <Check className="w-4 h-4" />
                </button>
              ),
              title: <span className={w.bought ? "line-through text-gray-400" : ""}>{w.name}</span>,
              subtitle: w.bought ? "Sudah terbeli!" : PRIO[w.priority],
              right: <span className="text-sm font-semibold text-gray-600 whitespace-nowrap">{rupiah(w.price)}</span>,
            })}
          />
        </div>
      </Section>
    </div>
  );
}
